<template>
  <div class="generate-image-container">
    <div class="generate-image-content">
      <div class="generate-image-header">
        <h2>图片生成设置</h2>
        <p>配置图片生成相关参数</p>
      </div>
      
      <el-form :model="form" label-width="120px">
        <el-form-item label="默认模型">
          <el-select v-model="form.model" placeholder="选择默认模型" style="width: 100%">
            <el-option v-for="model in models" :key="model.identifier" :label="model.name" :value="model.identifier">
              <div class="model-option">
                <img v-if="model.image_url" :src="model.image_url" alt="模型图片" class="model-icon">
                <span class="model-name">{{ model.name }}</span>
                <el-tag v-if="model.is_hot" size="small" type="danger" class="model-tag">热门</el-tag>
                <el-tag v-if="model.is_new" size="small" type="primary" class="model-tag">新品</el-tag>
              </div>
            </el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item label="预设提示词">
          <el-switch v-model="form.enablePresetPrompt" active-text="启用" inactive-text="禁用"></el-switch>
        </el-form-item>
        
        <el-form-item label="预设提示词内容">
          <el-input
            v-model="form.presetPrompt"
            type="textarea"
            :rows="4"
            placeholder="请输入预设提示词，会与用户输入的提示词组合"
          ></el-input>
        </el-form-item>
        
        <el-form-item label="生成限制">
          <el-input-number v-model="form.generateLimit" :min="1" :max="100" :step="1"></el-input-number>
          <span style="margin-left: 10px;">次/天</span>
        </el-form-item>
        
        <el-form-item label="生成超时时间">
          <el-input-number v-model="form.timeout" :min="1" :max="600" :step="1"></el-input-number>
          <span style="margin-left: 10px;">秒</span>
        </el-form-item>
        
        <el-form-item label="图片尺寸选项">
          <div class="size-management">
            <el-button type="primary" size="small" @click="addSize">添加尺寸</el-button>
          </div>
          <el-table :data="imageSizes" style="width: 50%; margin-top: 10px;" v-loading="sizesLoading">
            <el-table-column prop="name" label="尺寸名称" width="150"></el-table-column>
            <el-table-column prop="width" label="宽度" width="80"></el-table-column>
            <el-table-column prop="height" label="高度" width="80"></el-table-column>
            <el-table-column prop="aspect_ratio" label="宽高比" width="80"></el-table-column>
            <el-table-column prop="sort_order" label="排序" width="80"></el-table-column>
            <el-table-column prop="status" label="状态" width="80">
              <template slot-scope="scope">
                <el-switch
                  v-model="scope.row.status"
                  @change="updateSizeStatus(scope.row)"
                ></el-switch>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="150">
              <template slot-scope="scope">
                <div style="display: flex; gap: 8px;">
                  <el-button size="small" type="primary" @click="editSize(scope.row)">编辑</el-button>
                  <el-button size="small" type="danger" @click="deleteSize(scope.row.id)">删除</el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </el-form-item>
        
        <el-form-item label="默认图片尺寸">
          <el-select v-model="form.defaultImageSize" placeholder="选择默认图片尺寸">
            <el-option v-for="size in activeSizes" :key="size.name" :label="size.name" :value="size.name"></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item>
          <el-button type="primary" @click="saveSettings">保存设置</el-button>
          <el-button @click="resetForm">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 添加/编辑尺寸对话框 -->
    <el-dialog
      :title="sizeDialogTitle"
      :visible.sync="sizeDialogVisible"
      width="450px"
    >
      <el-form :model="sizeForm" :rules="sizeRules" ref="sizeForm" label-width="80px">
        <el-form-item label="宽度" prop="width">
          <el-input-number v-model="sizeForm.width" :min="1" :max="8000" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item label="高度" prop="height">
          <el-input-number v-model="sizeForm.height" :min="1" :max="8000" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item label="尺寸名称" prop="name">
          <el-input v-model="sizeForm.name" placeholder="如：1024x1024 (1:1)"></el-input>
        </el-form-item>
        <el-form-item label="宽高比" prop="aspect_ratio">
          <el-input v-model="sizeForm.aspect_ratio" placeholder="如：1:1"></el-input>
        </el-form-item>
        <el-form-item label="排序" prop="sort_order">
          <el-input-number v-model="sizeForm.sort_order" :min="0" :max="100" style="width: 100%"></el-input-number>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="sizeDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitSizeForm">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import appCache from '../../utils/appCache'

export default {
  name: 'GenerateImage',
  data() {
    return {
      models: [],
      imageSizes: [],
      sizesLoading: false,
      sizeDialogVisible: false,
      isEditSize: false,
      editSizeId: null,
      sizeForm: {
        width: 1024,
        height: 1024,
        name: '',
        aspect_ratio: '1:1',
        sort_order: 0
      },
      sizeRules: {
        width: [
          { required: true, message: '请输入宽度', trigger: 'blur' }
        ],
        height: [
          { required: true, message: '请输入高度', trigger: 'blur' }
        ],
        name: [
          { required: true, message: '请输入尺寸名称', trigger: 'blur' }
        ],
        aspect_ratio: [
          { required: true, message: '请输入宽高比', trigger: 'blur' }
        ],
        sort_order: [
          { required: true, message: '请输入排序', trigger: 'blur' }
        ]
      },
      form: {
        model: '',
        enablePresetPrompt: false,
        presetPrompt: '请生成高质量、细节丰富、符合要求的图片',
        generateLimit: 100,
        timeout: 60,
        defaultImageSize: '1024x1024 (1:1)'
      }
    }
  },
  computed: {
    sizeDialogTitle() {
      return this.isEditSize ? '编辑尺寸' : '添加尺寸'
    },
    activeSizes() {
      return this.imageSizes.filter(s => s.status)
    }
  },
  mounted() {
    this.loadModels()
    this.loadImageSizes()
    this.loadConfig()
  },
  methods: {
    async loadModels() {
      try {
        const imageModels = await appCache.getModelsByCategory('image')
        this.models = imageModels
      } catch (error) {
        console.error('加载图片模型失败:', error)
      }
    },
    async loadConfig() {
      try {
        const response = await axios.get('/admin/config/image')
        if (response.data.code === 200) {
          const config = response.data.data
          this.form.model = config.model || ''
          this.form.enablePresetPrompt = config.enablePresetPrompt || false
          this.form.presetPrompt = config.presetPrompt || '请生成高质量、细节丰富、符合要求的图片'
          this.form.generateLimit = config.generateLimit || 100
          this.form.timeout = config.timeout || 60
          this.form.defaultImageSize = config.defaultImageSize || '1024x1024 (1:1)'
        }
      } catch (error) {
        console.error('加载配置失败:', error)
      }
    },
    async loadImageSizes() {
      this.sizesLoading = true
      try {
        const response = await axios.get('/api/admin/image-sizes')
        if (response.data.code === 200) {
          this.imageSizes = response.data.image_sizes
        }
      } catch (error) {
        console.error('加载图片尺寸失败:', error)
      } finally {
        this.sizesLoading = false
      }
    },
    addSize() {
      this.isEditSize = false
      this.editSizeId = null
      this.sizeForm = {
        width: 1024,
        height: 1024,
        name: '',
        aspect_ratio: '1:1',
        sort_order: 0
      }
      this.sizeDialogVisible = true
    },
    editSize(row) {
      this.isEditSize = true
      this.editSizeId = row.id
      this.sizeForm = {
        width: row.width,
        height: row.height,
        name: row.name,
        aspect_ratio: row.aspect_ratio,
        sort_order: row.sort_order
      }
      this.sizeDialogVisible = true
    },
    async submitSizeForm() {
      this.$refs.sizeForm.validate(async valid => {
        if (valid) {
          try {
            if (this.isEditSize) {
              await axios.put(`/api/admin/image-sizes/${this.editSizeId}`, this.sizeForm)
              this.$message.success('更新成功')
            } else {
              await axios.post('/api/admin/image-sizes', this.sizeForm)
              this.$message.success('创建成功')
            }
            this.sizeDialogVisible = false
            this.loadImageSizes()
          } catch (error) {
            console.error('提交失败:', error)
            const message = error.response?.data?.detail || '操作失败'
            this.$message.error(message)
          }
        }
      })
    },
    async updateSizeStatus(row) {
      try {
        await axios.put(`/api/admin/image-sizes/${row.id}`, {
          status: row.status
        })
        this.$message.success('更新成功')
      } catch (error) {
        console.error('更新状态失败:', error)
        this.$message.error('更新状态失败')
        row.status = !row.status
      }
    },
    async deleteSize(id) {
      this.$confirm('确定要删除该尺寸吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await axios.delete(`/api/admin/image-sizes/${id}`)
          this.$message.success('删除成功')
          this.loadImageSizes()
        } catch (error) {
          console.error('删除失败:', error)
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },
    saveSettings() {
      axios.post('/admin/config/image', {
        model: this.form.model,
        enablePresetPrompt: this.form.enablePresetPrompt,
        presetPrompt: this.form.presetPrompt,
        generateLimit: this.form.generateLimit,
        timeout: this.form.timeout,
        defaultImageSize: this.form.defaultImageSize
      }).then(response => {
        if (response.data.code === 200) {
          this.$message.success('设置保存成功')
        } else {
          this.$message.error(response.data.message || '保存失败')
        }
      }).catch(error => {
        console.error('保存失败:', error)
        this.$message.error('保存失败')
      })
    },
    resetForm() {
      this.$message.info('表单已重置')
    }
  }
}
</script>

<style scoped>
.generate-image-container {
  padding: 20px 0;
}

.generate-image-content {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  color: #333;
}

.generate-image-header {
  margin-bottom: 30px;
}

.generate-image-header h2 {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.generate-image-header p {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.size-management {
  margin-bottom: 10px;
}

.model-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.model-icon {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  object-fit: cover;
}

.model-name {
  flex: 1;
}

.model-tag {
  margin-left: 4px;
}
</style>
